import React, { useState, useEffect } from 'react';
import { useAuth, createApiClient } from '../App';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ChartBar,
  ForkKnife,
  Star,
  Receipt,
  Users,
  TrendUp,
  CalendarBlank,
  SpinnerGap,
  Trophy,
  MapPin,
  CookingPot,
  CurrencyCircleDollar
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from 'recharts';

const COLORS = ['#A94438', '#4B5945', '#C4A35A', '#7A7873', '#D1E2C4', '#E8DCD0', '#8B362C', '#3E4A39'];

const Stats = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const api = createApiClient(token);

  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState('cuisine');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, analyticsRes] = await Promise.all([
        api.get('/stats'),
        api.get('/stats/analytics')
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F5] flex items-center justify-center">
        <SpinnerGap className="w-12 h-12 animate-spin text-[#A94438]" />
      </div>
    );
  }

  const StatCard = ({ icon: Icon, value, label, color = '#A94438' }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-elevated p-4 sm:p-5"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
          <Icon weight="duotone" className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-heading font-bold text-[#1A1918]">
        {value !== null && value !== undefined ? value : '-'}
      </div>
      <div className="text-xs text-[#9A9590] mt-0.5">{label}</div>
    </motion.div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#EAE8E3] rounded-lg px-3 py-2 shadow-lg">
        <p className="text-sm font-medium text-[#1A1918]">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-xs text-[#7A7873]">
            {p.name}: <span className="font-semibold" style={{ color: p.color }}>{p.value}</span>
          </p>
        ))}
      </div>
    );
  };

  const cuisineData = analytics?.cuisine_breakdown || [];
  const suburbData = analytics?.suburb_breakdown || [];

  return (
    <div className="container-app py-6 sm:py-8 pb-8" data-testid="stats-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1A1918]">
          Dining Stats
        </h1>
        <p className="text-sm text-[#7A7873] mt-1">Your dining journey at a glance</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard icon={ForkKnife} value={stats?.visited_this_month} label="Visits this month" color="#A94438" />
        <StatCard icon={CalendarBlank} value={stats?.visited_this_year} label="Visits this year" color="#4B5945" />
        <StatCard icon={Star} value={stats?.average_rating} label="Average rating" color="#A94438" />
        <StatCard icon={Receipt} value={stats?.total_spend ? `R${(stats.total_spend / 1000).toFixed(1)}k` : null} label="Total spend" color="#4B5945" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard icon={Users} value={stats?.average_spend_per_person ? `R${stats.average_spend_per_person.toFixed(0)}` : null} label="Avg per person" color="#7A7873" />
        <StatCard icon={TrendUp} value={stats?.total_visits} label="Total visits" color="#A94438" />
        <StatCard icon={Trophy} value={stats?.best_rated?.length || 0} label="Top rated spots" color="#4B5945" />
      </div>

      {/* Analytics Section */}
      {analytics && (cuisineData.length > 0 || suburbData.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-elevated p-4 sm:p-6 mb-6 sm:mb-8"
          data-testid="analytics-section"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
              <ChartBar weight="duotone" className="w-5 h-5 text-[#A94438]" />
              Analytics
            </h2>
            <div className="flex bg-[#F5F1EC] rounded-lg p-0.5">
              <button
                onClick={() => setActiveAnalyticsTab('cuisine')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeAnalyticsTab === 'cuisine' ? 'bg-white text-[#1A1918] shadow-sm' : 'text-[#7A7873]'}`}
                data-testid="analytics-tab-cuisine"
              >
                <CookingPot weight="bold" className="w-3.5 h-3.5 inline mr-1" />
                Cuisine
              </button>
              <button
                onClick={() => setActiveAnalyticsTab('suburb')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeAnalyticsTab === 'suburb' ? 'bg-white text-[#1A1918] shadow-sm' : 'text-[#7A7873]'}`}
                data-testid="analytics-tab-suburb"
              >
                <MapPin weight="bold" className="w-3.5 h-3.5 inline mr-1" />
                Suburb
              </button>
            </div>
          </div>

          {activeAnalyticsTab === 'cuisine' && cuisineData.length > 0 && (
            <div data-testid="cuisine-analytics">
              {/* Visits by Cuisine Chart */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-[#7A7873] mb-3">Visits by Cuisine</h3>
                <div className="h-[220px] sm:h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cuisineData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EAE8E3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7A7873' }} interval={0} angle={-35} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11, fill: '#9A9590' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="visits" name="Visits" fill="#A94438" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Cuisine breakdown table */}
              <div className="space-y-2" data-testid="cuisine-table">
                {cuisineData.map((item, idx) => (
                  <div key={item.name} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#F9F8F5] transition-colors">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span className="text-sm font-medium text-[#1A1918]">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#7A7873]">
                      <span>{item.visits} visit{item.visits !== 1 ? 's' : ''}</span>
                      {item.spend > 0 && <span>R{item.spend.toLocaleString()}</span>}
                      {item.avg_rating && (
                        <span className="flex items-center gap-0.5 text-[#A94438]">
                          <Star weight="fill" className="w-3 h-3" />
                          {item.avg_rating}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeAnalyticsTab === 'suburb' && suburbData.length > 0 && (
            <div data-testid="suburb-analytics">
              {/* Visits by Suburb Chart */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-[#7A7873] mb-3">Visits by Suburb</h3>
                <div className="h-[220px] sm:h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={suburbData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EAE8E3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7A7873' }} interval={0} angle={-35} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11, fill: '#9A9590' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="visits" name="Visits" fill="#4B5945" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Suburb breakdown table */}
              <div className="space-y-2" data-testid="suburb-table">
                {suburbData.map((item, idx) => (
                  <div key={item.name} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#F9F8F5] transition-colors">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span className="text-sm font-medium text-[#1A1918]">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#7A7873]">
                      <span>{item.visits} visit{item.visits !== 1 ? 's' : ''}</span>
                      {item.spend > 0 && <span>R{item.spend.toLocaleString()}</span>}
                      {item.avg_rating && (
                        <span className="flex items-center gap-0.5 text-[#A94438]">
                          <Star weight="fill" className="w-3 h-3" />
                          {item.avg_rating}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {((activeAnalyticsTab === 'cuisine' && cuisineData.length === 0) ||
            (activeAnalyticsTab === 'suburb' && suburbData.length === 0)) && (
            <div className="text-center py-8 text-[#9A9590]">
              <ChartBar weight="duotone" className="w-10 h-10 mx-auto mb-2 text-[#E8DCD0]" />
              <p className="text-sm">No data yet. Start logging visits to see analytics.</p>
            </div>
          )}
        </motion.div>
      )}

      {/* Best Rated & Most Revisited */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Best Rated */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-elevated p-4 sm:p-6"
        >
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Star weight="fill" className="w-5 h-5 text-[#A94438]" />
            Best Rated
          </h2>

          {stats?.best_rated && stats.best_rated.length > 0 ? (
            <div className="space-y-2">
              {stats.best_rated.map((restaurant, index) => (
                <div
                  key={restaurant.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9F8F5] cursor-pointer transition-colors"
                  onClick={() => navigate(`/restaurants/${restaurant.id}`)}
                  data-testid={`best-rated-${index}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#E8DCD0] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm text-[#1A1918] truncate">{restaurant.name}</h3>
                    {restaurant.suburb && (
                      <p className="text-xs text-[#7A7873]">{restaurant.suburb}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-[#A94438] flex-shrink-0">
                    <Star weight="fill" className="w-4 h-4" />
                    <span className="font-semibold text-sm">{restaurant.aggregate_rating}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#7A7873]">
              <Star weight="duotone" className="w-10 h-10 mx-auto mb-2 text-[#E8DCD0]" />
              <p className="text-sm">No rated restaurants yet</p>
            </div>
          )}
        </motion.div>

        {/* Most Revisited */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-elevated p-4 sm:p-6"
        >
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <TrendUp weight="duotone" className="w-5 h-5 text-[#4B5945]" />
            Most Revisited
          </h2>

          {stats?.most_revisited && stats.most_revisited.length > 0 ? (
            <div className="space-y-2">
              {stats.most_revisited.map((restaurant, index) => (
                <div
                  key={restaurant.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9F8F5] cursor-pointer transition-colors"
                  onClick={() => navigate(`/restaurants/${restaurant.id}`)}
                  data-testid={`most-revisited-${index}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#D1E2C4] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm text-[#1A1918] truncate">{restaurant.name}</h3>
                    {restaurant.suburb && (
                      <p className="text-xs text-[#7A7873]">{restaurant.suburb}</p>
                    )}
                  </div>
                  <div className="text-[#4B5945] font-semibold text-sm flex-shrink-0">
                    {restaurant.visit_count} visits
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#7A7873]">
              <ForkKnife weight="duotone" className="w-10 h-10 mx-auto mb-2 text-[#E8DCD0]" />
              <p className="text-sm">No revisited restaurants yet</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Stats;
